import { useCallback, useEffect } from 'react';
import { parseGrade, formatGrade } from '../utils/gpa';

export const useGPA = (assignments, courses, setCourses, settings) => {
  // GPA calculation utilities
  const parseGradeWrapper = useCallback((grade) => {
    try {
      return parseGrade(grade);
    } catch (error) {
      console.error('Error parsing grade:', error);
      return null;
    }
  }, []);

  const formatGradeWrapper = useCallback((percentage) => {
    try {
      return formatGrade(percentage, settings);
    } catch (error) {
      console.error('Error formatting grade:', error);
      return 'N/A';
    }
  }, [settings]);

  // Update course grades whenever assignments change
  const updateCourseGrade = useCallback((courseId) => {
    // Skip if settings aren't loaded yet
    if (!settings || !settings.gpa) return;

    const courseAssignments = assignments.filter(a =>
      (a.course && a.course._id === courseId) ||
      (typeof a.course === 'string' && a.course === courseId)
    );

    if (courseAssignments.length > 0) {
      // Calculate average grade from assignments that have grades
      const gradedAssignments = courseAssignments.filter(a => a.grade !== null && a.grade !== undefined);
      if (gradedAssignments.length > 0) {
        const totalScore = gradedAssignments.reduce((sum, a) => {
          const gradeValue = parseGradeWrapper(a.grade);
          return sum + (gradeValue || 0);
        }, 0);
        const averageGrade = totalScore / gradedAssignments.length;
        const letterGrade = formatGradeWrapper(averageGrade);

        setCourses(prevCourses => prevCourses.map(c =>
          c._id === courseId ? { ...c, grade: letterGrade } : c
        ));
      } else {
        // Clear course grade if no assignments have grades
        setCourses(prevCourses => prevCourses.map(c =>
          c._id === courseId ? { ...c, grade: null } : c
        ));
      }
    }
  }, [assignments, settings, parseGradeWrapper, formatGradeWrapper, setCourses]);

  // Update course grades whenever assignments change
  useEffect(() => {
    // Skip if settings aren't loaded yet
    if (!settings || !settings.gpa) return;

    const courseIds = [...new Set(
      assignments
        .filter(a => a.course) // Only assignments with courses
        .map(a => a.course._id || a.course) // Get course ID safely
        .filter(id => id) // Filter out null/undefined IDs
    )];
    courseIds.forEach(courseId => {
      updateCourseGrade(courseId);
    });
  }, [assignments, settings, updateCourseGrade]);

  return {
    parseGrade: parseGradeWrapper,
    formatGrade: formatGradeWrapper,
    updateCourseGrade
  };
};